<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.gameDesigner.hero.title')"
      :paragraph="t('jc.view.gameDesigner.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.gameDesigner.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.gameDesigner.hero.float.alt'),
      }"
      :label="t('jc.view.gameDesigner.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.gameDesigner.ourOffer.title')"
      :paragraph="t('jc.view.gameDesigner.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.gameDesigner.ourConditions.title')"
      :paragraph="t('jc.view.gameDesigner.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.gameDesigner.yourTasks.title')"
      :paragraph="t('jc.view.gameDesigner.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.gameDesigner.impressions.title')"
      :paragraph="t('jc.view.gameDesigner.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.gameDesigner.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.gameDesigner.howToApply.title')"
      :paragraph="t('jc.view.gameDesigner.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.gameDesigner.moreInformation.title')"
        :paragraph="t('jc.view.gameDesigner.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/questmaster.png'

definePageMeta({
  nameKey: 'participate-gameDesigner',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.gameDesigner.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'dragon',
    text: t('jc.view.gameDesigner.ourOffer.iconHighlight.creativeDevelopment.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.gameDesigner.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.gameDesigner.ourOffer.iconHighlight.newTechniques.text'),
  },
  {
    icon: 'cubes',
    text: t('jc.view.gameDesigner.ourOffer.iconHighlight.straightWork.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.gameDesigner.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.gameDesigner.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.gameDesigner.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.gameDesigner.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.gameDesigner.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.gameDesigner.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'paintbrush-pencil',
    text: t('jc.view.gameDesigner.ourOffer.icon.passionatePeople.text'),
  },
  {
    icon: 'paintbrush',
    text: t('jc.view.gameDesigner.ourOffer.icon.resourcepack.text'),
  },
  {
    icon: 'comments',
    text: t('jc.view.gameDesigner.ourOffer.icon.exchange.text'),
  },
  {
    icon: 'fingerprint',
    text: t('jc.view.gameDesigner.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.gameDesigner.ourOffer.icon.closeCooperation.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.gameDesigner.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.gameDesigner.ourConditions.icon.fantasy.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.gameDesigner.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.gameDesigner.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.gameDesigner.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.gameDesigner.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'gamepad-modern',
    text: t('jc.view.gameDesigner.yourTasks.icon.think.text'),
  },
  {
    icon: 'people-arrows-left-right',
    text: t('jc.view.gameDesigner.yourTasks.icon.write.text'),
  },
  {
    icon: 'list-check',
    text: t('jc.view.gameDesigner.yourTasks.icon.design.text'),
  },
  {
    icon: 'scroll',
    text: t('jc.view.gameDesigner.yourTasks.icon.elaborate.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.gameDesigner.yourTasks.icon.invent.text'),
  },
  {
    icon: 'sparkles',
    text: t('jc.view.gameDesigner.yourTasks.icon.shape.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'contentplate',
    mediaType: 'image/*',
    src: 'images/impressions/quest_master/contentplate.png',
  },
  {
    identifier: 'tutorial',
    mediaType: 'image/*',
    src: 'images/impressions/quest_master/tutorial_menu.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.gameDesigner.howToApply.icon.firstContact.title'),
    text: t('jc.view.gameDesigner.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.gameDesigner.howToApply.icon.conversation.title'),
    text: t('jc.view.gameDesigner.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.gameDesigner.howToApply.icon.wait.title'),
    text: t('jc.view.gameDesigner.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.gameDesigner.howToApply.icon.trainee.title'),
    text: t('jc.view.gameDesigner.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.gameDesigner.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.gameDesigner.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.gameDesigner.moreInformation.card.team.text'),
    label: t('jc.view.gameDesigner.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.gameDesigner.moreInformation.card.weekly_review.text'),
    label: t('jc.view.gameDesigner.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.gameDesigner.moreInformation.card.plan.text'),
    label: t('jc.view.gameDesigner.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.gameDesigner.moreInformation.card.stackShare.text'),
    label: t('jc.view.gameDesigner.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.gameDesigner.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.gameDesigner.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.gameDesigner.moreInformation.card.blog.text'),
    label: t('jc.view.gameDesigner.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
